<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useWindowSize } from '@vueuse/core';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import Button from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import EmailTranscriptModal from '../widgets/conversation/EmailTranscriptModal.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);

const [showActionsDropdown, toggleDropdown] = useToggle();
const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const { width } = useWindowSize();
const isSmallScreen = computed(
  () => width.value <= wootConstants.SMALL_SCREEN_BREAKPOINT
);

const currentChat = computed(() => getters.getSelectedChat.value);
const isContactMuted = computed(() => currentChat.value?.muted);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const handleMuteToggle = () => {
  closeDropdown();
  if (isContactMuted.value) {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
    return;
  }
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const handleSendTranscript = () => {
  closeDropdown();
  toggleEmailModal(true);
};

const toggleStatus = (status, snoozedUntil) => {
  closeDropdown();
  isLoading.value = true;
  store
    .dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status,
      snoozedUntil,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
      isLoading.value = false;
    });
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      await toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      await toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div class="relative flex items-center justify-end resolve-actions">
    <div
      class="rounded-lg shadow outline-1 outline flex-shrink-0"
      :class="!showOpenButton ? 'outline-n-container' : 'outline-transparent'"
    >
      <Button
        v-if="isOpen"
        :label="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        size="sm"
        color="slate"
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdResolveConversation"
      />
      <Button
        v-else-if="isResolved"
        :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        size="sm"
        color="slate"
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="showOpenButton"
        :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        size="sm"
        color="slate"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-if="showAdditionalActions"
        ref="arrowDownButtonRef"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="sm"
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        color="slate"
        trailing-icon
        @click="openDropdown"
      />
    </div>
    <div
      v-if="showActionsDropdown && !isSmallScreen"
      v-on-clickaway="closeDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
            ghost
            slate
            sm
            start
            icon="i-lucide-alarm-clock-minus"
            class="w-full"
            @click="() => openSnoozeModal()"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
            ghost
            slate
            sm
            start
            icon="i-lucide-circle-dot-dashed"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
    <TeleportWithDirection to="body">
      <div
        v-if="showActionsDropdown && isSmallScreen"
        class="fixed inset-0 z-[1000] bg-n-alpha-black1/60 backdrop-blur-[2px]"
        @click.self="closeDropdown"
      >
        <div class="flex flex-col h-full bg-n-solid-1">
          <header
            class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
          >
            <h3 class="text-base font-medium text-n-slate-12">
              {{ t('CONVERSATION.HEADER.MORE_ACTIONS') }}
            </h3>
            <Button
              icon="i-lucide-x"
              slate
              ghost
              size="md"
              @click="closeDropdown"
            />
          </header>
          <div class="flex-1 overflow-y-auto p-4">
            <div class="flex flex-col gap-3">
              <Button
                v-if="!isPending"
                :label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
                icon="i-lucide-alarm-clock-minus"
                slate
                ghost
                class="justify-start"
                @click="
                  () => {
                    closeDropdown();
                    openSnoozeModal();
                  }
                "
              />
              <Button
                v-if="!isPending"
                :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
                icon="i-lucide-circle-dot-dashed"
                slate
                ghost
                class="justify-start"
                @click="
                  () => {
                    toggleStatus(wootConstants.STATUS_TYPE.PENDING);
                  }
                "
              />
              <div class="pt-4 mt-4 border-t border-n-weak">
                <div class="flex flex-col gap-3">
                  <Button
                    :label="
                      isContactMuted
                        ? t('CONTACT_PANEL.UNMUTE_CONTACT')
                        : t('CONTACT_PANEL.MUTE_CONTACT')
                    "
                    :icon="
                      isContactMuted
                        ? 'i-lucide-volume-1'
                        : 'i-lucide-volume-off'
                    "
                    slate
                    ghost
                    class="justify-start"
                    @click="handleMuteToggle"
                  />
                  <Button
                    :label="t('CONTACT_PANEL.SEND_TRANSCRIPT')"
                    icon="i-lucide-share"
                    slate
                    ghost
                    class="justify-start"
                    @click="handleSendTranscript"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </TeleportWithDirection>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="() => toggleEmailModal(false)"
    />
  </div>
</template>
